import { useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules'
import { FiSmartphone, FiTrendingUp, FiZap } from 'react-icons/fi'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-fade'
import '../styles/swiper-custom.css'
import '../styles/device-showcase.css'

import Reveal from './ui/Reveal'

import dispositivo1 from '../assets/images/dispositivo1.png'
import dispositivo2 from '../assets/images/dispositivo2.png'
import dispositivo3 from '../assets/images/dispositivo3.png'

const DEVICE_IMAGES = [
  { src: dispositivo1, alt: 'Dispositivo SysMV' },
  { src: dispositivo2, alt: 'Interface de monitoramento' },
  { src: dispositivo3, alt: 'Sistema em uso' },
]

const POINTS = [
  { icon: FiSmartphone, text: 'Acesse de qualquer dispositivo' },
  { icon: FiTrendingUp, text: 'Dados e gráficos em tempo real' },
  { icon: FiZap, text: 'Alertas e notificações instantâneas' },
]

const DeviceShowcase = () => {
  const [activeIndex, setActiveIndex] = useState(0)

  return (
    <section
      id="dispositivo"
      className="device-showcase section-padding bg-white dark:bg-gray-900 relative"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" aria-hidden />
      <div className="container-custom px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center w-full min-w-0">
          {/* Texto – esquerda (no mobile fica em cima, centralizado) */}
          <Reveal direction="left">
            <div className="text-center lg:text-left lg:pr-4 xl:pr-8 min-w-0">
              <p className="text-xs font-extrabold text-accent uppercase tracking-[0.25em] mb-3 sm:mb-4">
                Conheça o Produto
              </p>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white leading-[1.15] tracking-tight">
                O Dispositivo
              </h2>
              <p className="mt-3 sm:mt-4 text-sm text-gray-600 dark:text-gray-300 leading-relaxed max-w-md mx-auto lg:mx-0">
                Tecnologia de ponta para monitoramento de volume. Interface simples,
                dados precisos e acesso de qualquer lugar.
              </p>
              <div className="mt-5 sm:mt-6 space-y-2.5 sm:space-y-3">
                {POINTS.map((item, index) => {
                  const Icon = item.icon
                  return (
                    <Reveal key={index} direction="left" delay={index * 80}>
                      <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-gray-50 dark:bg-gray-800/60 border border-transparent dark:border-gray-700/50 transition-transform duration-300 hover:scale-[1.02] group/point">
                        <span className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-gradient-to-br from-accent/25 to-accent/10 dark:from-accent/30 dark:to-accent/15 flex items-center justify-center group-hover/point:scale-110 transition-transform duration-300">
                          <Icon className="w-5 h-5 text-accent" />
                        </span>
                        <span className="text-sm text-gray-800 dark:text-gray-100 font-medium text-left">
                          {item.text}
                        </span>
                      </div>
                    </Reveal>
                  )
                })}
              </div>
            </div>
          </Reveal>

          {/* Carrossel – direita */}
          <Reveal direction="right" delay={100}>
            <div className="device-carousel-wrap w-full min-w-0 device-carousel-shadow">
              <div className="device-carousel-card">
                {/* Barra tipo browser / app */}
                <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-800/80 border-b border-gray-200/80 dark:border-gray-700/50">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400 dark:bg-red-500" aria-hidden />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400 dark:bg-amber-500" aria-hidden />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 dark:bg-emerald-500" aria-hidden />
                  </div>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium ml-2 truncate">SysMV — Monitoramento</span>
                </div>
                <Swiper
                  modules={[EffectFade, Navigation, Pagination, Autoplay]}
                  effect="fade"
                  fadeEffect={{ crossFade: true }}
                  speed={500}
                  loop
                  grabCursor
                  autoplay={{
                    delay: 4000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }}
                  navigation={{
                    prevEl: '.device-carousel-prev',
                    nextEl: '.device-carousel-next',
                  }}
                  pagination={{
                    el: '.device-carousel-pagination',
                    clickable: true,
                    bulletClass: 'device-bullet',
                    bulletActiveClass: 'device-bullet-active',
                  }}
                  onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                  className="device-swiper w-full"
                >
                  {DEVICE_IMAGES.map((item, index) => (
                    <SwiperSlide key={index}>
                      <div className="device-slide-inner">
                        <img
                          src={item.src}
                          alt={item.alt}
                          className="device-slide-img"
                          loading={index === 0 ? 'eager' : 'lazy'}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>

                <div className="device-progress-wrap">
                  <div
                    key={activeIndex}
                    className="device-progress-fill"
                  />
                </div>

                <button
                  type="button"
                  className="device-carousel-prev"
                  aria-label="Anterior"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="device-carousel-next"
                  aria-label="Próximo"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                <div className="device-carousel-pagination" />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

export default DeviceShowcase
